/**
 * 换绑手机弹窗
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Smartphone, Shield, Loader2, Check, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import securityService from '../../services/securityService';

const ChangePhoneModal = ({ isOpen, onClose, currentPhone, onSuccess }) => {
    const { token } = useAuth();

    // 步骤：1-验证原手机，2-输入新手机，3-验证新手机
    const [step, setStep] = useState(currentPhone ? 1 : 2);

    const [formData, setFormData] = useState({
        oldCode: '',
        newPhone: '',
        newCode: '',
    });

    const [oldCountdown, setOldCountdown] = useState(0);
    const [newCountdown, setNewCountdown] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [phoneMasked, setPhoneMasked] = useState('');

    // 重置状态
    const resetState = useCallback(() => {
        setStep(currentPhone ? 1 : 2);
        setFormData({ oldCode: '', newPhone: '', newCode: '' });
        setOldCountdown(0);
        setNewCountdown(0);
        setError('');
        setPhoneMasked('');
    }, [currentPhone]);

    useEffect(() => {
        if (!isOpen) {
            resetState();
        }
    }, [isOpen, resetState]);

    // 倒计时
    useEffect(() => {
        if (oldCountdown > 0) {
            const timer = setTimeout(() => setOldCountdown(oldCountdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [oldCountdown]);

    useEffect(() => {
        if (newCountdown > 0) {
            const timer = setTimeout(() => setNewCountdown(newCountdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [newCountdown]);

    // 发送原手机验证码
    const sendOldPhoneCode = async () => {
        setSending(true);
        setError('');
        try {
            const res = await securityService.sendSmsForChangePhone(token, { action: 'verify_old' });
            setOldCountdown(60);
            setPhoneMasked(res.phone_masked);
        } catch (err) {
            setError(err.response?.data?.error || '发送失败');
        } finally {
            setSending(false);
        }
    };

    // 发送新手机验证码
    const sendNewPhoneCode = async () => {
        if (!formData.newPhone || formData.newPhone.length !== 11) {
            setError('请输入正确的手机号');
            return;
        }
        setSending(true);
        setError('');
        try {
            const res = await securityService.sendSmsForChangePhone(token, {
                action: 'verify_new',
                phone: formData.newPhone
            });
            setNewCountdown(60);
            setPhoneMasked(res.phone_masked);
        } catch (err) {
            setError(err.response?.data?.error || '发送失败');
        } finally {
            setSending(false);
        }
    };

    // 验证原手机
    const verifyOldPhone = () => {
        if (!formData.oldCode || formData.oldCode.length !== 6) {
            setError('请输入6位验证码');
            return;
        }
        setError('');
        setStep(2);
    };

    // 提交换绑
    const handleSubmit = async () => {
        if (!formData.newPhone || formData.newPhone.length !== 11) {
            setError('请输入正确的手机号');
            return;
        }
        if (!formData.newCode || formData.newCode.length !== 6) {
            setError('请输入6位验证码');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await securityService.changePhone(token, {
                new_phone: formData.newPhone,
                new_code: formData.newCode,
                old_code: currentPhone ? formData.oldCode : undefined,
            });

            onSuccess?.();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || '换绑失败');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="fixed inset-0 flex items-center justify-center z-50 p-4"
                    >
                        <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
                            {/* 头部 */}
                            <div className="relative px-6 pt-6 pb-4 border-b border-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                                        <Smartphone className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">
                                            {currentPhone ? '换绑手机' : '绑定手机'}
                                        </h2>
                                        <p className="text-xs text-gray-500">
                                            {step === 1 ? '验证原手机号' : '输入新手机号'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                                >
                                    <X className="w-4 h-4 text-gray-500" />
                                </button>
                            </div>

                            <div className="p-6 space-y-4">
                                {/* 步骤指示 */}
                                {currentPhone && (
                                    <div className="flex items-center justify-center gap-2 mb-4">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-400'
                                            }`}>1</div>
                                        <div className={`w-8 h-0.5 ${step >= 2 ? 'bg-blue-500' : 'bg-gray-200'}`} />
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-400'
                                            }`}>2</div>
                                    </div>
                                )}

                                {/* 错误提示 */}
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600"
                                    >
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        <span>{error}</span>
                                    </motion.div>
                                )}

                                {/* 步骤1：验证原手机 */}
                                {step === 1 && currentPhone && (
                                    <>
                                        <div className="text-center py-4">
                                            <div className="text-sm text-gray-500 mb-2">原手机号</div>
                                            <div className="text-lg font-bold text-gray-900">
                                                {phoneMasked || currentPhone}
                                            </div>
                                        </div>

                                        <div className="flex gap-3">
                                            <input
                                                type="text"
                                                value={formData.oldCode}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    oldCode: e.target.value.replace(/\D/g, '').slice(0, 6)
                                                }))}
                                                placeholder="请输入验证码"
                                                className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                maxLength={6}
                                            />
                                            <button
                                                type="button"
                                                onClick={sendOldPhoneCode}
                                                disabled={sending || oldCountdown > 0}
                                                className={`px-4 py-3 rounded-xl text-sm font-semibold whitespace-nowrap ${oldCountdown > 0
                                                        ? 'bg-gray-100 text-gray-400'
                                                        : 'bg-blue-500 text-white hover:bg-blue-600'
                                                    }`}
                                            >
                                                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> :
                                                    oldCountdown > 0 ? `${oldCountdown}s` : '获取验证码'}
                                            </button>
                                        </div>

                                        <button
                                            onClick={verifyOldPhone}
                                            disabled={!formData.oldCode || formData.oldCode.length !== 6}
                                            className="w-full py-3.5 rounded-xl font-bold text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            <span>下一步</span>
                                            <ArrowRight className="w-4 h-4" />
                                        </button>
                                    </>
                                )}

                                {/* 步骤2：输入新手机 */}
                                {step === 2 && (
                                    <>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1.5">新手机号</label>
                                            <input
                                                type="tel"
                                                value={formData.newPhone}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    newPhone: e.target.value.replace(/\D/g, '').slice(0, 11)
                                                }))}
                                                placeholder="请输入新手机号"
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                maxLength={11}
                                            />
                                        </div>

                                        <div className="flex gap-3">
                                            <div className="relative flex-1">
                                                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                                <input
                                                    type="text"
                                                    value={formData.newCode}
                                                    onChange={(e) => setFormData(prev => ({
                                                        ...prev,
                                                        newCode: e.target.value.replace(/\D/g, '').slice(0, 6)
                                                    }))}
                                                    placeholder="验证码"
                                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    maxLength={6}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={sendNewPhoneCode}
                                                disabled={sending || newCountdown > 0 || formData.newPhone.length !== 11}
                                                className={`px-4 py-3 rounded-xl text-sm font-semibold whitespace-nowrap ${newCountdown > 0 || formData.newPhone.length !== 11
                                                        ? 'bg-gray-100 text-gray-400'
                                                        : 'bg-blue-500 text-white hover:bg-blue-600'
                                                    }`}
                                            >
                                                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> :
                                                    newCountdown > 0 ? `${newCountdown}s` : '获取验证码'}
                                            </button>
                                        </div>

                                        <button
                                            onClick={handleSubmit}
                                            disabled={loading || !formData.newPhone || !formData.newCode}
                                            className={`w-full py-3.5 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${loading || !formData.newPhone || !formData.newCode
                                                    ? 'bg-gray-300 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-lg'
                                                }`}
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                    <span>处理中...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Check className="w-5 h-5" />
                                                    <span>确认{currentPhone ? '换绑' : '绑定'}</span>
                                                </>
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default ChangePhoneModal;
